/*
  Bid offer engine — pure pricing/probability logic for the bid dialog Step 1.

  Kept out of snippets/dialog-bid.liquid so it can be unit-tested (the Vitest
  harness loads assets/*.js as text into jsdom; inline Liquid scripts can't be).
  Le regole di offerta (constraintsFor) vivono qui: dialog-bid.liquid tiene
  solo il gate degli 8.000 EUR, sopra il quale il bid lascia il posto al form
  "Request a price".

  All prices are in major currency units (euros, not cents), matching
  bidDialogData in dialog-bid.liquid.
*/
(function () {
  "use strict";

  // Difesa per quando `constraints` arriva null/undefined: coincide con la
  // regola "listino sconosciuto" (-10% suggerito, -15% minimo), la piu'
  // prudente fra quelle pubblicate. constraintsFor() non torna mai null, ma
  // anchors()/evaluate() sono chiamate anche prima che la variante sia nota.
  var DEFAULTS = { min: 0, max: 0.1, input: 0.15 };

  /**
   * Derive the offer anchors for a variant from the matrix constraints.
   * @param {number} price - variant price
   * @param {{min:number,max:number,input:number}|null} constraints
   * @returns {{floor:number, good:number, better:number, maxDiscountPct:number}}
   *   floor  - lowest manual offer accepted (price * (1 - input))
   *   good   - "good offer" anchor (price * (1 - max))
   *   better - soglia del chip "molto probabile"; con min = 0 coincide
   *            col prezzo pieno (price * (1 - min))
   */
  function anchors(price, constraints) {
    var c = constraints || DEFAULTS;
    var min = typeof c.min === "number" ? c.min : DEFAULTS.min;
    var max = typeof c.max === "number" ? c.max : DEFAULTS.max;
    var input = typeof c.input === "number" ? c.input : DEFAULTS.input;
    return {
      floor: Math.round(price * (1 - input)),
      good: Math.round(price * (1 - max)),
      better: Math.round(price * (1 - min)),
      // Deepest manual discount allowed (floor) — for "up to −X%" copy on limits.
      maxDiscountPct: Math.round(input * 100),
      // Discount at the "good offer" anchor — for "offers up to −X% are
      // usually accepted" copy (matches the chip's good/low boundary).
      acceptDiscountPct: Math.round(max * 100),
    };
  }

  /**
   * Evaluate an offer against the anchors.
   * @param {number} offer - user's offer (major units)
   * @param {number} price - variant price
   * @param {{min:number,max:number,input:number}|null} constraints
   * @returns {{
   *   state: 'empty'|'counter'|'ok',
   *   level: 'high'|'good'|'low'|null,
   *   floor:number, good:number, better:number, maxDiscountPct:number,
   *   clamped:number, discountPct:number
   * }}
   *   state 'counter' = below floor (offer starts at `floor`);
   *   level maps to acceptance-probability chips:
   *     high  → offer >= better anchor (or >= list price)
   *     good  → offer >= good anchor
   *     low   → between floor and good anchor
   *   clamped = max(floor, offer), what a "start from our minimum" action
   *   should set the offer to.
   */
  function evaluate(offer, price, constraints) {
    var a = anchors(price, constraints);
    var value = Number(offer);
    var base = {
      floor: a.floor,
      good: a.good,
      better: a.better,
      maxDiscountPct: a.maxDiscountPct,
      acceptDiscountPct: a.acceptDiscountPct,
    };

    if (!isFinite(value) || value <= 0) {
      return Object.assign(base, {
        state: "empty",
        level: null,
        clamped: a.floor,
        discountPct: 0,
      });
    }

    var clamped = Math.max(a.floor, value);
    var discountPct = price > 0 ? Math.max(0, (price - value) / price) : 0;

    if (value < a.floor) {
      return Object.assign(base, {
        state: "counter",
        level: null,
        clamped: clamped,
        discountPct: discountPct,
      });
    }

    var level = "low";
    if (value >= a.better || value >= price) level = "high";
    else if (value >= a.good) level = "good";

    return Object.assign(base, {
      state: "ok",
      level: level,
      clamped: clamped,
      discountPct: discountPct,
    });
  }

  /**
   * "Add a little, get a lot": gifted bike discount tied to a service kit.
   * gift = min(serviceFull - add, base - floor), never negative. Display-only
   * until the bids-service materializes the discount in the draft order.
   * @param {{offer:number, floor:number, serviceFull:number, add:number}} p
   *   add - what the customer actually pays on top of the offer for the kit
   * @returns {{gift:number, base:number, bikeNet:number, payMore:number}}
   */
  function computeGift(p) {
    var base = Math.max(p.floor, p.offer);
    var giftNominal = Math.max(0, (p.serviceFull || 0) - (p.add || 0));
    var gift = Math.min(giftNominal, Math.max(0, base - p.floor));
    return {
      gift: gift,
      base: base,
      bikeNet: base - gift,
      payMore: Math.max(0, (p.add || 0)),
    };
  }

  /** Suggerito + minimo accettato nella forma attesa da anchors(). */
  function band(suggested, floor) {
    // min resta 0 di proposito: le regole pubblicano un solo numero, quindi
    // l'ancora "better" coincide col prezzo pieno e il chip "molto probabile"
    // scatta solo a prezzo di listino, senza inventare una soglia intermedia.
    return { min: 0, max: suggested, input: floor };
  }

  /**
   * Soglie dell'offerta per una variante.
   *
   * Due numeri per famiglia: il suggerito (`max`) e il minimo accettato
   * (`input`), sempre in percentuale sul prezzo di vendita.
   *
   * Rispetto alla matrice precedente sparisce l'asse prezzo: le percentuali
   * sono le stesse a qualsiasi importo. Sopra gli 8.000 EUR non si arriva
   * nemmeno qui, perche' dialog-bid.liquid mostra il form "Request a price"
   * al posto del bid.
   *
   * @param {{price:number, comparePrice:number, isBkrm:boolean,
   *          isNew:boolean}} data - prezzi in unita' intere (euro).
   * @returns {{min:number,max:number,input:number}} mai null.
   */
  function constraintsFor(data) {
    var price = Number(data.price);
    var compare = Number(data.comparePrice);

    // Bici Bikeroom: flat, nuove e usate uguali, lo sconto non conta.
    if (data.isBkrm) return band(0.08, 0.12);

    // Usate dei dealer: flat.
    if (!data.isNew) return band(0.1, 0.15);

    // Nuove dei dealer: le fasce hanno bisogno di un listino attendibile.
    // Assente, zero o sotto il prezzo significa che l'MSRP non lo sappiamo:
    // era il ~12% delle bici dealer e finiva su un minimo del -20%, troppo
    // aggressivo, con i dealer che rifiutavano l'offerta.
    if (!isFinite(compare) || compare <= 0 || compare < price) {
      return band(0.1, 0.15);
    }

    // Estremo superiore incluso: uno sconto del 15% sta in "fino al 15%",
    // non nella fascia successiva. Listino uguale al prezzo = non scontata.
    var discount = ((compare - price) / compare) * 100;
    if (discount <= 0) return band(0.2, 0.25);
    if (discount <= 15) return band(0.16, 0.2);
    if (discount <= 30) return band(0.12, 0.16);
    if (discount <= 45) return band(0.08, 0.12);
    return band(0.04, 0.08);
  }

  /**
   * Normalizza un id cliente al formato GID atteso dal backend bike-seller.
   * Il configuratore passa l'id numerico grezzo (data-customer-id); il backend
   * valida `shopifyUserId` come `gid://shopify/Customer/<id>` e rifiuta con 400
   * l'id nudo. Idempotente: se è già un gid (o vuoto/null) lo lascia intatto.
   */
  function toCustomerGid(id) {
    if (id == null || id === "") return id;
    var s = String(id);
    return s.indexOf("gid://") === 0 ? s : "gid://shopify/Customer/" + s;
  }

  /**
   * Body per POST /api/v1/bid/request — parità ESATTA con la vecchia
   * submitFormData della modal: il kit (service) viene fuso dentro upsellId,
   * shopifyUserId/variantId/upsellId diventano gid, boxVariantId resta RAW
   * (id numerico), compareAtPrice è il prezzo di listino Shopify (naming storico).
   * @param {object} p - campi del formData + contesto:
   *   shopifyUserId, variantId, value, currency, expiration (giorni "3"|"7"),
   *   service, boxKey, boxVariantId, hasBox, hasWarranty, isInstantBuy,
   *   upsellId[], compareAtPrice, marketName, countryCode, fxRate,
   *   vies?, vatNumber?, now (epoch ms — parametrico per i test)
   */
  function buildBidPayload(p) {
    var upsell = (p.upsellId || []).slice();
    if (p.service) upsell.push(p.service);
    var body = {
      shopifyUserId: toCustomerGid(p.shopifyUserId),
      variantId: "gid://shopify/ProductVariant/" + p.variantId,
      upsellId: upsell.map(function (id) {
        return "gid://shopify/ProductVariant/" + id;
      }),
      value: String(p.value),
      currency: p.currency,
      expiration: new Date(p.now + parseInt(p.expiration, 10) * 86400000).toISOString(),
      hasWarranty: !!p.hasWarranty,
      hasBox: !!p.hasBox,
      boxKey: p.boxKey || null,
      boxVariantId: p.boxVariantId || null,
      isInstantBuy: !!p.isInstantBuy,
      compareAtPrice: p.compareAtPrice,
      marketName: p.marketName,
      countryCode: p.countryCode,
      fxRate: p.fxRate,
    };
    if (p.vies && p.vatNumber) {
      body.vies = true;
      body.vatNumber = p.vatNumber;
    }
    return body;
  }

  window.BkrmBidOffer = {
    anchors: anchors,
    evaluate: evaluate,
    computeGift: computeGift,
    constraintsFor: constraintsFor,
    buildBidPayload: buildBidPayload,
  };
})();
